import AccountSelect from "../AccountSelect";
import TagsSelect from "../TagsSelect";
import CompanySelect from "../CompanySelect";
import PersonSelect from "../PersonSelect";
import CategorySelect from "../CategorySelect";
import BoolToggle from "../BoolToggle";
import { Button, Col, DatePicker, Divider, Form,
  Input, Row, Space, Tooltip
} from 'antd';
import ProjectSelect from '../ProjectSelect';
import PeriodSelect from '../PeriodSelect';
import ProjectSplitsField from './ProjectSplitsField';
import ProjectSplitsModal from './ProjectSplitsModal';
import { APIClient } from "../../helpers/api_helper";
import { BranchesOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';

const api = new APIClient();

const { TextArea } = Input;

const OverrideLabel = ({ label, field, overriddenFields }) => (
  <span>
    {label}
    {overriddenFields?.includes(field) && (
      <Tooltip title="Vrednost je bila ročno spremenjena.">
        <EditOutlined style={{ color: '#1677ff', marginLeft: 4 }} />
      </Tooltip>
    )}
  </span>
);

const validateSplits = async (_, splits) => {
  if (!splits || splits.length === 0) return; // single-project mode
  if (splits.length < 2)
    throw new Error('Potrebujete vsaj dva projekta.');
  const projectIds = splits.map((s) => s.project);
  if (projectIds.some((p) => p == null))
    throw new Error('Izberite projekt za vsako vrstico.');
  if (new Set(projectIds).size !== projectIds.length)
    throw new Error('Projekt se pojavi večkrat.');
  const sum = splits.reduce((a, s) => a + Number(s.split_percent || 0), 0);
  if (Math.abs(sum - 100) > 0.01)
    throw new Error(`Vsota odstotkov mora biti 100% (trenutno ${sum}%).`);
};

const DefaultFields = ({
  isIntentEditable,
  repeatPeriod,
  typeValue,
  taxIntentInfoComponent,
  massEdit,
  overriddenFields,
  splitModalOpen,
  setSplitModalOpen,
  isEdit,
  editedIntentId,
  onSplitsUpdated,
  splitEditMode,
  activeEditDimension,
}) => {
  const form = Form.useFormInstance();
  const grossAmount = Form.useWatch('amount', form);
  const datesDisabled = splitEditMode === 'child';
  // Only lock repetition fields when EDITING an existing split intent with the
  // split info box active. Creation must allow split + repeat together.
  const wizardFieldsDisabled = isEdit && activeEditDimension === 'split';
  return (
    <>
      {isIntentEditable && (
        <>
          <Row>
            <Col span={12}>
              {(typeValue == 'EXPENSE' || massEdit) && (
                <Form.Item
                  name="date_invoice_recieved"
                  label={<OverrideLabel label="Prejeto" field="date_invoice_recieved" overriddenFields={overriddenFields} />}
                  rules={massEdit ? [] : [{ required: true, message: 'Prosim vnesite datum prejema' }]}
                  help={<small>Datum prejetega računa.</small>}
                  >
                    <DatePicker style={{ width: '100%' }} disabled={datesDisabled} getPopupContainer={(trigger) => trigger.parentElement} />
                </Form.Item>
              )}
              {(typeValue == 'INCOME' || massEdit) && (
                <Form.Item
                  name="date_service_performed"
                  label={<OverrideLabel label="Opravljeno" field="date_service_performed" overriddenFields={overriddenFields} />}
                  rules={massEdit ? [] : [{ required: true, message: 'Prosim vnesite datum opravljene storitve' }]}
                  help={<small>Datum opravljene storitve.</small>}
                  >
                  <DatePicker style={{ width: '100%' }} disabled={datesDisabled} getPopupContainer={(trigger) => trigger.parentElement} />
                </Form.Item>
              )}
            </Col>
            {(typeValue != null || massEdit) && (
              <Col span={typeValue == 'TAX' ? 24 : 12}>
                <Form.Item
                  name="date_to_be_paid"
                  label={<OverrideLabel label="Realizirano" field="date_to_be_paid" overriddenFields={overriddenFields} />}
                  rules={massEdit ? [] : [{ required: true, message: 'Prosim vnesite datum plačila' }]}
                  help={<small>Datum realizacije.</small>}
                  >
                  <DatePicker style={{ width: '100%' }} disabled={datesDisabled} getPopupContainer={(trigger) => trigger.parentElement} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={[12, 12]} style={{ marginTop: 12, marginBottom: 24 }}>
            <Col span={12}>
              <Form.Item name="is_paid" initialValue={massEdit ? null : false} noStyle>
                <BoolToggle label="Plačano" allowNull={massEdit} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_issued" initialValue={massEdit ? null : false} noStyle>
                <BoolToggle label="Izdano" allowNull={massEdit} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row>
            <Col span={24}>
              <Form.Item
                name="title"
                label={<OverrideLabel label="Naziv" field="title" overriddenFields={overriddenFields} />}
                rules={[{ required: true, message: 'Prosim vnesite naziv' }]}
              >
                <Input size="large" placeholder="Vnesite naziv" />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item
                name="descr"
                rules={[]}
              >
                <TextArea placeholder={'Opis...'} rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row>
            <Col span={24}>
              <Form.Item name="account" label="Račun">
                <AccountSelect />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item name="tag" label="Oznaka">
                <TagsSelect />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item name="company" label="Podjetje">
                <CompanySelect />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item name="person" label="Oseba">
                <PersonSelect />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item name="category" label="Kategorija">
                <CategorySelect />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item label="Projekt" shouldUpdate>
                {() => {
                  const splits = form.getFieldValue('project_splits') || [];
                  const hasSplits = splits.length > 0;
                  const canSplit =
                    (typeValue === 'EXPENSE' || typeValue === 'INCOME') && !massEdit;
                  return (
                    <Space.Compact style={{ display: 'flex', width: '100%' }}>
                      {hasSplits ? (
                        <Input
                          readOnly
                          value={`Razdeljen med ${splits.length} projektov`}
                          style={{ flex: 1 }}
                        />
                      ) : (
                        <Form.Item name="project" noStyle>
                          <ProjectSelect />
                        </Form.Item>
                      )}
                      {canSplit && (
                        <Tooltip
                          title={
                            hasSplits ? 'Uredi razdelitev' : 'Razdeli med projekte'
                          }
                        >
                          <Button
                            icon={<BranchesOutlined />}
                            type={hasSplits ? 'primary' : 'default'}
                            onClick={() => setSplitModalOpen(true)}
                          />
                        </Tooltip>
                      )}
                      {hasSplits && !isEdit && (
                        <Tooltip title="Razveljavi razdelitev">
                          <Button
                            icon={<CloseOutlined />}
                            onClick={() =>
                              form.setFieldsValue({ project_splits: [] })
                            }
                          />
                        </Tooltip>
                      )}
                    </Space.Compact>
                  );
                }}
              </Form.Item>

              <Form.Item
                name="project_splits"
                hidden
                rules={[{ validator: validateSplits }]}
              >
                <ProjectSplitsField hidden />
              </Form.Item>

              <ProjectSplitsModal
                open={!!splitModalOpen}
                onCancel={() => setSplitModalOpen(false)}
                onConfirm={async (splits) => {
                  // split_amount is a UI-only convenience field; only project +
                  // split_percent are persisted.
                  const cleaned = splits.map((s) => ({
                    project: s.project,
                    split_percent: s.split_percent,
                  }));
                  if (isEdit && editedIntentId) {
                    await api.post(
                      `/api/intents/${editedIntentId}/resplit/`,
                      { project_splits: cleaned },
                      { successMsg: 'Razdelitev posodobljena' }
                    );
                    onSplitsUpdated?.();
                  } else {
                    form.setFieldsValue({ project_splits: cleaned, project: null });
                  }
                  setSplitModalOpen(false);
                }}
                initialValue={form.getFieldValue('project_splits')}
                amount={grossAmount}
                readOnly={false}
              />
            </Col>
          </Row>

          <Divider />

          <Row>
            <Col span={12}>
              <Form.Item name="invoice_number" label="Št. računa">
                <Input placeholder="Vnesite številko računa" />
              </Form.Item>
            </Col>
          </Row>


          {!massEdit && (
            <Row>
              <Col span={12}>
                <Form.Item name="repeat_period" label="Ponavljanje">
                  <PeriodSelect disabled={wizardFieldsDisabled} />
                </Form.Item>
              </Col>
              {repeatPeriod && (
                <Col span={12}>
                  <Form.Item
                    name="repeat_to_date"
                    label="Ponavljaj do"
                    rules={[{ required: true, message: 'Prosim vnesite končni datum ponavljanja' }]}
                  >
                    <DatePicker style={{ width: '100%' }} disabled={wizardFieldsDisabled} getPopupContainer={(trigger) => trigger.parentElement} />
                  </Form.Item>
                </Col>
              )}
            </Row>
          )}
        </>
      )}
    </>
  );
};

export default DefaultFields;
